import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getReferenceCharacterLimits } from '~/selectors/label-settings.selectors';
import { getOrdersCount } from '~/selectors/orders.selectors';
import { getCurrentPlan } from '~/selectors/plans.selectors';
import { getUser } from '~/selectors/user.selectors';
import { getAppcuesSignature } from '~/services/appcues.service';
import { getFullName } from '~/types/User';

import { newRelic } from '../newRelic/newRelic';

export const AppcuesMonitor = () => {
  const Appcues = window.Appcues;

  const [isInitialized, setIsInitialized] = useState(false);

  const user = useSelector(getUser);
  const currentPlan = useSelector(getCurrentPlan);
  const ordersCount = useSelector(getOrdersCount);
  const refCharSize = useSelector(getReferenceCharacterLimits);

  const { data: appcuesTokenData, error } = useQuery({
    enabled: !!user && !!CONFIG.appcues.enabled,
    queryFn: () => getAppcuesSignature(),
    queryKey: ['appcues_signature'],
  });

  useEffect(() => {
    const { appcues } = CONFIG;
    if (!appcues || !appcues.enabled) return;

    const { scriptId } = appcues;
    const scriptSrc = `//fast.appcues.com/${scriptId}.js`;

    if (document.querySelector(`script[src="${scriptSrc}"]`)) return;

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.onload = () => {
      setIsInitialized(true);
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!Appcues) return;
    if (!isInitialized) return;
    if (!user) return;
    if (error)
      newRelic.noticeError('Failed to acquire appcues user signature', { error: error as Error });
    if (!appcuesTokenData) return;

    window.AppcuesSettings = {
      enableURLDetection: true,
      userIdSignature: appcuesTokenData.data.token,
    };

    const userData = {
      dateJoined: user.dateJoined.toISOString(),
      defaultSenderAddressCountry: user.address?.country?.iso2,
      email: user.email,
      hasAchDiscount: user.invoiceAdjustments.includes('ACH_DISCOUNT'),
      isPro: currentPlan?.plan.isPro || currentPlan?.plan.isFullAccess || false,
      lifetimeVolSegmentMax: user.lifetimeVolSegmentMax || 0,
      lifetimeVolSegmentMin: user.lifetimeVolSegmentMin || 0,
      name: getFullName(user),
      orderCount: ordersCount,
      revenueTier: parseInt(user.revenueTierName, 10) || 5,
      ...refCharSize,
    };

    Appcues.identify(String(user.id), userData);
  }, [
    Appcues,
    isInitialized,
    user,
    currentPlan,
    ordersCount,
    appcuesTokenData,
    error,
    refCharSize,
  ]);

  return null;
};
